import { LoadingOverlay } from '@mantine/core'
import { ModalDrawer } from 'components/ModalDrawer/ModalDrawer'
import { translate } from 'i18n/i18n'
import { useEffect, useState } from 'react'
import { SharedSettings } from 'settings/SharedSettings'
import { useGroupsAndContacts } from '../../ContactsAndGroups/ContactsAndGroupsContext'
import type { AlertTypeId, AlertWithPossibleId } from '../_types'
import { AlertsSettings } from '../settings/alertSettings'
import { convertAPIAlertToInitivalValues } from './_utils/convertAPIAlertToInitivalValues'
import { AlertTypeAndPestType } from './AlertForm/AlertTypeAndPestType/AlertTypeAndPestType'
import { V3AlertForm } from './AlertForm/V3AlertForm'
import { WeatherForecastAlertForm } from './AlertForm/WeatherForecastAlertForm'

export type SleepUnitsType = 'minutes' | 'hours' | 'days'

export const ModalDrawerCreateOrEditAlert = ({
  opened,
  alertId,
  onClose,
  selectedAlert,
  refreshAlerts,
}: {
  opened: boolean
  alertId: string | null
  onClose: () => void
  selectedAlert: AlertWithPossibleId | null
  refreshAlerts: () => void
}) => {
  const { groups } = useGroupsAndContacts()

  const [initialState, setInitialState] = useState(
    convertAPIAlertToInitivalValues({ initialValues: selectedAlert, allGroups: groups || [] }),
  )

  const [alertTypeId, setAlertTypeId] = useState<AlertTypeId | null>(initialState.alertTypeId)
  const [insectId, setInsectId] = useState<number | null>(initialState.insectId ?? null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitHandler, setSubmitHandler] = useState<(() => unknown) | null>(null)

  useEffect(() => {
    const newInitialState = convertAPIAlertToInitivalValues({
      initialValues: selectedAlert,
      allGroups: groups || [],
    })

    setInitialState(newInitialState)

    setAlertTypeId(newInitialState.alertTypeId)

    setInsectId(newInitialState.insectId ?? null)
  }, [selectedAlert])

  const onSetAlertTypeId = (value: AlertTypeId) => {
    setAlertTypeId(value)
  }

  const handleOnClose = () => {
    setAlertTypeId(null)

    onClose()
  }

  const submitButtonText = alertId
    ? translate.phrases.banyanApp('Update Alert')
    : translate.phrases.banyanApp('Create Alert')

  const titleText = alertId
    ? translate.phrases.banyanApp('Edit Alert "{{name}}"', { name: initialState.name })
    : translate.phrases.banyanApp('Create New Alert')

  const requiresInsectId = alertTypeId && AlertsSettings.alertTypesWithPestTypes[alertTypeId]
  const readyToSubmit = alertTypeId && (!requiresInsectId || insectId)

  return (
    <ModalDrawer
      title={titleText}
      opened={opened}
      onClose={handleOnClose}
      zIndex={SharedSettings.DEFAULT_MODAL_DRAWER_Z_INDEX + 100}
      primaryButtonText={readyToSubmit ? submitButtonText : undefined}
      primaryButtonDisabled={isSubmitting}
      primaryButtonOnPress={() => {
        // Instead of a ref, lift the submit handler up via a prop
        if (typeof submitHandler === 'function') {
          submitHandler()
        }
      }}
    >
      <LoadingOverlay visible={isSubmitting} />

      <div css={{ padding: 20 }}>
        {/* TYPE */}
        <AlertTypeAndPestType
          alertTypeId={alertTypeId}
          onSetAlertTypeId={onSetAlertTypeId}
          insectId={insectId}
          setInsectId={setInsectId}
          alertId={alertId}
          key={`${alertId}-${insectId}`} // Force re-render when alertId or insectId changes
        />

        {alertTypeId &&
          alertTypeId !== 'weatherForecastAlert' &&
          initialState.alertTypeId !== 'weatherForecastAlert' && (
            <V3AlertForm
              // Pass a setter prop to receive the submit handler
              setSubmitHandler={setSubmitHandler}
              opened={opened}
              alertId={alertId}
              insectId={insectId}
              setInsectId={setInsectId}
              onClose={handleOnClose}
              initialState={initialState}
              selectedAlert={selectedAlert}
              alertTypeId={alertTypeId}
              submitting={isSubmitting}
              setIsSubmitting={setIsSubmitting}
              refreshAlerts={refreshAlerts}
            />
          )}
        {alertTypeId && alertTypeId === 'weatherForecastAlert' && (
          <WeatherForecastAlertForm
            opened={opened}
            alertId={alertId}
            onClose={onClose}
            // @ts-expect-error
            initialState={initialState}
            selectedAlert={selectedAlert}
            submitting={isSubmitting}
            setIsSubmitting={setIsSubmitting}
            setSubmitHandler={setSubmitHandler}
            refreshAlerts={refreshAlerts}
          />
        )}
      </div>
    </ModalDrawer>
  )
}
